import React, { useState, useRef } from "react";
import styled from "styled-components";
import { FiChevronDown } from "react-icons/fi";
import { Button } from "./Button";

// Define our own ButtonProps since it's not exported from Button.tsx
type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "danger"
  | "success"
  | "info"
  | "warning"
  | "gradient";

interface MenuButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  $variant?: ButtonVariant;
  size?: "sm" | "md" | "lg" | "xl";
  $size?: "sm" | "md" | "lg" | "xl";
  icon?: React.ReactNode;
  children?: React.ReactNode;
  circular?: boolean;
  $circular?: boolean;
  fullWidth?: boolean;
  $fullWidth?: boolean;
  options: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  }[];
}

const MenuContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const CaretContainer = styled.span`
  display: flex;
  align-items: center;
  margin-left: 8px;
  transition: transform 0.2s ease;
`;

const PopoverContainer = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  background: white;
  right: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  min-width: 180px;
  z-index: 1000;
  overflow: hidden;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
  transform: ${({ isOpen }) =>
    isOpen ? "translateY(0)" : "translateY(-10px)"};
  transition: all 0.2s ease;
`;

const OptionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const OptionItem = styled.li`
  padding: 0;
`;

const OptionButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  padding: 12px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontFamily.sans};
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.text};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.verae[50]};
  }

  .option-icon {
    margin-right: 8px;
    display: flex;
    align-items: center;
  }
`;

export const MenuButton: React.FC<MenuButtonProps> = ({
  options,
  children,
  icon,
  ...buttonProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOptionClick = (onClick: () => void) => {
    onClick();
    setIsOpen(false);
  };

  return (
    <MenuContainer ref={containerRef}>
      <Button {...buttonProps} onClick={toggleMenu} icon={icon}>
        {children}
        <CaretContainer
          style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0)" }}
        >
          <FiChevronDown size={16} />
        </CaretContainer>
      </Button>

      <PopoverContainer isOpen={isOpen}>
        <OptionsList>
          {options.map((option, index) => (
            <OptionItem key={index}>
              <OptionButton onClick={() => handleOptionClick(option.onClick)}>
                {option.icon && (
                  <span className="option-icon">{option.icon}</span>
                )}
                {option.label}
              </OptionButton>
            </OptionItem>
          ))}
        </OptionsList>
      </PopoverContainer>
    </MenuContainer>
  );
};
